// NexusTable/createRowData.js

import { addPaddingRightToLastCell, getCellWithTooltip, setStats } from "../../modules/table.funcs"
import { formatDate, fromDateUnknown, getAge, getCapacities, getNameProp, getStatusLevel, getYearsAndMonths } from "../../modules/utilities"
import { CATEGORY, COLOR, DATES, FORMATTING, JARGON, TENSE, POSITION, POSITION_ENUM, ROLE_STATUS, SEX, SEX_ENUM, TENSE_ENUM } from "../../modules/constants"
import { getRowFromCapacity, getIndexCell } from "../../components/layout/NexusTableFunctions"
import { notEmpty } from "../../modules/utilities"
import HOOK from "../../components/hooks"

const { border1, black, bold, date, left, normal, number, right, string } = FORMATTING
const borderLeft = border1

// used for CSV
export const createNexusRowDataForCsv = props => {
  // console.log('createRowData props:', props)
  let connection = {}
  const { checkboxes, isCompany, items, lseCompany, maxLseData, showSecondDegree, stats, tense } = props

  const showEndColumn = tense[TENSE_ENUM.PAST] || tense[TENSE_ENUM.NOTICE]
  const rows = []
  items.filter(i => i.level > 0).forEach(item => {

    const showConnection = !showSecondDegree
      ? item.level === 1
      : item.level === 2 && (isCompany
        ? item.entity.category === CATEGORY.COMPANY
        : item.entity.category === CATEGORY.PERSON)

    if (showConnection) {

      const indexBit = item.entity.companyProps?.lse?.indexBit

      connection = showSecondDegree
        ? {}
        : isCompany
          ? Object.filter(props.connections, c => c.childId === item.id)
          : Object.filter(props.connections, c => c.parentId === item.id)

      if (notEmpty(connection)) {
        const capacities = getCapacities(connection, item.id)

        capacities.officer.forEach(role => {
          if (!!(checkboxes[HOOK.TENSE] & role.tenseBit) && !!(checkboxes[HOOK.POSITION] & role.typeBit)) {
            const row = getRowFromCapacity({ ...props, indexBit, role, item })
            rows.push(row)
            ++stats.numRows
            ++stats.numRoles
          }
        })

        capacities.psc.forEach(role => {
          if (!!(checkboxes[HOOK.TENSE] & role.tenseBit)
            && !!(checkboxes[HOOK.POSITION] & role.typeBit)
          ) {
            const row = getRowFromCapacity({ ...props, role, item })
            rows.push(row)
            ++stats.numRows
            ++stats.numRoles
          }
        })
      } else { // no connection - second degree!!

        const companyFontWeight = maxLseData && (item.entity.companyProps?.lse || item?.via?.indexBit)
          ? bold
          : normal // set FTSE companies text to bold

        const tenseColor = maxLseData
          ? (item.tense !== null) ? TENSE[item.tense].COLOR : "pink" // corporate officer had no tense - needs attention
          : TENSE[TENSE_ENUM.CURRENT].COLOR
        const statusColor = getStatusColor(item)
        const positionType = getPositionType(item) // ???? should be typeBit ??? Is this code ever called
        const positionColor = getPositionColor(positionType)
        const positionFontWeight = getPositionFontWeight(positionType, maxLseData)
        const rolesString = getRolesString(item)

        //#region  get the cells
        const nameCell = getNameCell(item, positionColor)
        const roleCell = getRoleCell(item, positionColor)
        const viaNodeCell = getViaCell(item)
        const startCell = getStartCell(item, tenseColor)
        // Set to DATES.FUTURE so that connectionEnd can be identified accordingly (using FUTURE_YEAR) in table.funcs
        item.connectionEnd = item.future ? DATES.FUTURE : item.connectionEnd
        const endCell = getEndCell(item, tenseColor)
        const overlapCell = getOverlapCell(item, stats, tenseColor)
        const ageCell = item.entity.personInfo
          ? getAgeCell(item, rolesString, stats)
          : getCellWithTooltip({ tooltip: JARGON.NOT_APPLICABLE })
        const compensationCell = getCompensationCell(item, stats)
        const sexCell = getSexCell(item, stats)
        const indexCell = getIndexCell(indexBit || item?.via?.indexBit, black)
        // const occupationCell = getOccupationCell (item,  JARGON.MORE_INFO)
        // const durationCell = getDurationCell(item, tenseColor, JARGON.MORE_INFO)
        // const overlapStartCell = getOverlapStartCell(item, tenseColor, JARGON.MORE_INFO)
        // const overlapEndCell = getOverlapEndCell(item, tenseColor, JARGON.MORE_INFO)
        // const addressCell = getAddressCell(item)
        //#endregion get the cells

        let row =
        {
          _id: item.entity._id,
          index: item.index,
          link: getLink(item),
          icon: item.entity.category,
          iconColor: statusColor,
          fontWeight: companyFontWeight === bold || positionFontWeight === bold ? bold : normal,
          isFavourite: item.entity.isFavourite,

          cellWithTooltips: isCompany
            ? showSecondDegree
              ? showEndColumn
                ? maxLseData
                  ? [nameCell, indexCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
                  : [nameCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
                : maxLseData
                  ? [nameCell, indexCell, viaNodeCell, startCell, overlapCell] // current, second degree
                  : [nameCell, viaNodeCell, startCell, overlapCell] // current, second degree
              : showEndColumn
                ? [nameCell, roleCell, startCell, endCell, overlapCell] // current & past, first degree only - OK
                : [nameCell, roleCell, startCell, overlapCell] // current, first degree only - OK
            : showSecondDegree
              ? showEndColumn
                ? maxLseData
                  ? [nameCell, viaNodeCell, indexCell, startCell, endCell, overlapCell] // current & past, second degree - OK
                  : [nameCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
                : maxLseData
                  ? [nameCell, viaNodeCell, indexCell, startCell, overlapCell] // current, second degree
                  : [nameCell, viaNodeCell, startCell, overlapCell] // current, second degree
              : showEndColumn
                ? [nameCell, roleCell, startCell, endCell, overlapCell] // current & past, first degree only - OK
                : [nameCell, roleCell, startCell, overlapCell], // current, first degree only  - OK
        }

        if (isCompany && !showSecondDegree) {
          if (lseCompany && maxLseData) {
            row.cellWithTooltips = [...row.cellWithTooltips, compensationCell, ageCell, sexCell]
          } else { // not Lse
            row.cellWithTooltips.push(ageCell)
          }
        }

        addPaddingRightToLastCell(row.cellWithTooltips)

        rows.push(row)
        ++stats.numRows
        ++stats.numRoles
      }
    }
  })

  return rows
}

const getAgeCell = (item, rolesString, stats) => {
  const age = getAge(item.entity.personInfo.dateOfBirth)
  setStats(age, stats.age, true)

  return getCellWithTooltip({
    tooltip: `${age} years old`, // - born in ${item.entity.personInfo.dateOfBirth}`,
    value: rolesString === "Secretary" ? JARGON.NOT_DISCLOSED : age
  }) // TODO - should be able to find if secretary also has a director role elsewhere, even in past
}
const getStatusColor = item => item.entity.category === CATEGORY.COMPANY
  ? COLOR[getStatusLevel(item.entity.companyProps.status)]
  : COLOR.UNKNOWN

const getPositionColor = positionType => POSITION[positionType].COLOR

const getPositionFontWeight = (positionType, maxLseData) =>
  (maxLseData && positionType >= 0 && POSITION[positionType].FONT_WEIGHT)
    ? POSITION[positionType].FONT_WEIGHT : normal

const getRolesString = item => item.level === 2
  ? item.via?.role : item.roles ? item.roles.join(", ") : ""

const getPositionType = item => item.entity.position && item.entity.position.type >= 0
  ? 2 ** item.entity.position.type // Convert item.entity.position.type to bit eg 0 > 1, 1 > 2, 2 > 4 etc
  : POSITION_ENUM.OTHER

const getCompensationCell = (item, stats) => {
  const compensation = getCompensation(item)
  // const tooltip = "single figure total remuneration"

  setStats(compensation, stats.compensation, true)

  return getCellWithTooltip({
    align: right,
    sortvalue: compensation > 0 ? compensation : 0,
    type: string,
    value: compensation > 0
      ? compensation.toLocaleString()
      : JARGON.UNKNOWN,
  })
}

const getCompensation = item => !!item.entity.position.compensationGBP
  ? item.entity.position.compensationGBP
  : JARGON.UNKNOWN

const getNameCell = (item, color) =>
  getCellWithTooltip({
    color,
    sortValue: getNameProp.toSort(item.entity.name),
    type: string,
    value: getNameProp.toPresent(item.entity.name),
  })

const getViaCell = item =>
  getCellWithTooltip({
    align: left,
    sortValue: getNameProp.toSort(item.via?.name),
    type: string,
    value: getNameProp.toPresent(item.via?.name) ?? "",
  })

const getStartCell = (item, color) => getCellWithTooltip({
  align: right,
  borderLeft,
  color,
  sortValue: item.connectionStart ? item.connectionStart.getTime() : 0,
  tooltip: item.connectionStart && !fromDateUnknown(item.connectionStart)
    ? formatDate(item.connectionStart, true) : "",
  type: date,
  value: item.connectionStart,
})

const getEndCell = (item, color) => getCellWithTooltip({
  align: right,
  tooltip: item.future
    ? ROLE_STATUS.FUTURE
    : item.connectionEnd
      ? formatDate(item.connectionEnd, true)
      : ROLE_STATUS.CURRENT,
  color,
  type: date,
  value: item.connectionEnd,
  sortValue: item.connectionEnd
    ? item.connectionEnd.getTime()
    : DATES.FUTURE.getTime(),
})

const getRoleCell = (item, color) => {
  // console.log('item.roles:',item.roles)
  const tooltip = item.roles ? item.roles.join(", ") : ""

  return getCellWithTooltip({
    color,
    type: string,
    tooltip,
    value: item.role,
  }) // HIGHLIGHT MISMATCHES
}

const getSexCell = (item, stats) => {
  const sex = item.entity.personInfo?.sex ?? SEX_ENUM.UNKNOWN

  if (sex === 0 || sex === 1) {
    stats.sex.total += sex
    ++stats.sex.numof
  }

  return getCellWithTooltip({
    align: right,
    tooltip: SEX[sex].NAME.toLowerCase(),
    type: string,
    value: SEX[sex].SHORT,
  })
}

const getOverlapCell = (item, stats, color) => {
  const overlapDuration = item.connectionOverlapDuration ? getYearsAndMonths(item.connectionOverlapStart, item.connectionOverlapEnd) : JARGON.UNKNOWN
  const overlapDurationSort = item.connectionOverlapDuration ? item.connectionOverlapDuration : 0
  // console.log("item.overlapDuration: " + item.connectionOverlapDuration)

  setStats(item.connectionOverlapDuration, stats.tenure, true)

  return getCellWithTooltip({
    color,
    prefix: fromDateUnknown(item.connectionStart) ? "at least" : null,
    sortValue: overlapDurationSort,
    type: number,
    value: overlapDuration,
  })
}

const getLink = item => `/${item.entity.category || item.category.keyValues["category"]}/${item.entity.ids.join(",")}`

/*
const getDurationCell = (item, connectionColor, nameTip) => getCellWithTooltip(nameTip, right, connectionColor, number, item.connectionDuration && item.connectionDuration.toFixed(1), item.connectionDuration)
const getOccupationCell = (item, nameTip) => getCellWithTooltip(nameTip, right, black, string, item.entity.occupation)
const getOverlapStartCell = (item, connectionColor, nameTip) => getCellWithTooltip(nameTip, right, connectionColor, date, item.connectionOverlapStart)
const getOverlapEndCell = (item, connectionColor, nameTip) => getCellWithTooltip(nameTip, right, connectionColor, date, item.connectionOverlapEnd)
const getAddressCell = item =>  getCellWithTooltip(JARGON.MORE_INFO, right, black, string,  item.entity.address.shortAddress, item.entity.address.shortAddress)
const getSex = item => item.entity.finnhubExecutive ? item.entity.finnhubExecutive.sex : '?'
*/

